import { Box, ThemeProvider, useTheme } from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import { FlexcellMark, useMergeBeat } from "../../ui/theme/FlexcellMark";
import { accentedConsoleTheme } from "../../ui/theme/consoleTheme";
import { useTokens } from "../../ui/theme/theme";

// ---
// The frame the console's own sign-in is drawn in.
// The form on the left, and on the right a sheet's grid with the mark set into four of its cells.
// The panel steps aside on a narrow screen, where it would only push the form down.
//
// Sign-in only. The pages somebody reaches from a mailed link use `LandingLayout`, which is a
// box and nothing else: this panel describes FlexAuth, which is right for an administrator who
// came here on purpose and wrong for an invitee who has never heard of us.
// ---

// one grid cell; the mark spans two, so each sparkle fills one
const CELL = 64;

// the mark moves once per page load, not again on each signed-out page within it
let greeted = false;

/** One beat on the first signed-out page of a load, and none on the ones after it. */
function useGreeting(): number {
  const [beat] = useState(greeted ? 0 : 1);
  useEffect(() => {
    greeted = true;
  }, []);
  return beat;
}

function GridPanel(): React.JSX.Element {
  const tokens = useTokens();
  const greeting = useMergeBeat(useGreeting());
  const rule = `color-mix(in srgb, ${tokens.line.default} 55%, ${tokens.line.faint})`;
  return (
    <Box
      aria-hidden
      sx={{
        display: { xs: "none", md: "block" },
        position: "relative",
        flex: "1 1 50%",
        m: 2.5,
        borderRadius: `${tokens.radius.xl}px`,
        border: `1px solid ${tokens.line.default}`,
        bgcolor: tokens.surface.railBg,
        overflow: "hidden",
        backgroundImage: `linear-gradient(${rule} 1px, transparent 1px), linear-gradient(90deg, ${rule} 1px, transparent 1px)`,
        backgroundSize: `${CELL}px ${CELL}px`,
        // a grid line through the centre, so the mark sits in whole cells
        backgroundPosition: `calc(50% + ${CELL / 2}px) calc(50% + ${CELL / 2}px)`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: `calc(50% - ${CELL * 2}px)`,
          transform: "translateX(-50%)",
          display: "flex",
        }}
      >
        <FlexcellMark
          size={CELL * 2}
          color={tokens.accent.base}
          title="FlexAuth"
          spin={greeting.spin}
          merged={greeting.merged}
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: `calc(50% + ${CELL / 2}px)`,
          transform: "translateX(-50%)",
          width: "min(420px, 80%)",
          textAlign: "center",
        }}
      >
        <Box
          sx={{ typography: "h2", fontWeight: tokens.weight.regular, color: tokens.ink.primary }}
        >
          Who can do what, across every flow.
        </Box>
        <Box sx={{ typography: "body1", color: tokens.ink.muted, mt: 2.5 }}>
          FlexAuth issues the tokens your apps and flows check — scoped to a tenant, a project and a
          role.
        </Box>
      </Box>
    </Box>
  );
}

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}): React.JSX.Element {
  const tokens = useTokens();
  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: tokens.surface.bg }}>
      <Box sx={{ flex: "1 1 50%", display: "flex", flexDirection: "column", p: 7, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <FlexcellMark size={20} color={tokens.accent.base} title="FlexAuth" />
          <Box sx={{ fontSize: 16, fontWeight: tokens.weight.semibold, color: tokens.ink.primary }}>
            FlexAuth
          </Box>
        </Box>

        <Box sx={{ flex: 1, display: "grid", placeItems: "center", py: 10 }}>
          <Box sx={{ width: "100%", maxWidth: 340 }}>
            <Box
              component="h1"
              sx={{
                typography: "h1",
                fontSize: "1.75rem",
                color: tokens.ink.primary,
                m: 0,
                mb: 2,
              }}
            >
              {title}
            </Box>
            {subtitle !== undefined && (
              <Box sx={{ typography: "body1", color: tokens.ink.muted, mb: 6 }}>{subtitle}</Box>
            )}
            {children}
          </Box>
        </Box>

        {footer !== undefined && (
          <Box sx={{ typography: "caption", color: tokens.ink.muted, textAlign: "center" }}>
            {footer}
          </Box>
        )}
      </Box>
      <GridPanel />
    </Box>
  );
}
